import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
  type MutableRefObject,
  type RefObject,
} from 'react'
import { useBackend } from '../../lib/session'
import { anchorIndex, ensureTrailingText, isEditable, isOutOfFlow, newBlockId, unfoldAnchorsIn, unwrapUnfold } from '../../lib/blocks'
import type { Block, EditableBlock, TextBlock, Thought } from '../../lib/types'
import { pastedParts } from '../../lib/paste'
import {
  caretAtEnd,
  caretAtStart,
  currentRange,
  htmlBeside,
  insertPlainText,
  isCollapsed,
  mergeInto,
  placeCaret,
  placeCaretAt,
  removeSlashToken,
  splitAtCaret,
  textBeforeCaret,
} from '../../components/editor/caret'
import { SLASH_LIMIT } from '../../components/editor/SlashMenu'
import { backspaceAt, deleteColumns, deleteForwardBlocked, groupOf, leaveColumns, removeCell } from '../../lib/columns'
import { groupEnd, pasteAcross } from './blockOps'
import type { Doc } from './useComposerDoc'

/**
 * The "/" query the caret sits at the end of: "/" at the start of the line or
 * after a space, then up to 40 characters of search (words with single spaces
 * between them), or null.
 */
export function slashQueryBefore(text: string): string | null {
  const m = text.replace(/\u00a0/g, ' ').match(/(?:^|\s)\/((?:[^\s/](?:[^\s/]| (?! ))*)?)$/)
  return m && m[1].length <= 40 ? m[1] : null
}

/** "#", "##" or "###" alone on a line: the heading level it asks for, else null. */
export function markdownHeadingLevel(text: string): 1 | 2 | 3 | null {
  const m = /^(#{1,3})$/.exec(text.replace(/ /g, ' ').trim())
  return m ? (m[1].length as 1 | 2 | 3) : null
}

/** A collapsed range at the end of `el`. */
function endOf(el: HTMLElement): Range {
  const r = document.createRange()
  r.selectNodeContents(el)
  r.collapse(false)
  return r
}

export const blockEl = (id: string) => document.querySelector<HTMLElement>(`[data-block="${id}"]`)
export const selectableEl = (id: string) => document.querySelector<HTMLElement>(`[data-selectable="${id}"]`)
const sceneLineEl = (id: string) => document.querySelector<HTMLElement>(`[data-scene-line="${id}"]`)

interface SlashState {
  blockId: string
  query: string
  index: number
}

interface PendingFocus {
  id: string
  /** The start, the end, or this many characters into the text. */
  where: 'start' | 'end' | number
  /** A scene's line rather than a paragraph. */
  scene?: boolean
}

interface Options {
  docRef: MutableRefObject<Doc>
  setBlocks: (fn: (bs: Block[]) => Block[]) => void
  /** An undoable block operation (a paste of several paragraphs is one). */
  blockOp: (fn: (bs: Block[]) => Block[]) => void
  subtitleRef: RefObject<HTMLElement | null>
  onSlashThought: (thought: Thought, blockId: string) => void
  onRemoveBlock: (id: string) => void
  /** Backspace or Delete from the text selected a card: the next press removes it. */
  onCardReached?: (id: string) => void
}

/**
 * The composer's editing state and the paragraph keyboard: which block is
 * selected, where the caret last was, the "/" menu, Enter/Backspace/paste
 * across blocks, and remounting blocks whose content changed from code.
 */
export function useBlockEditor({ docRef, setBlocks, blockOp, subtitleRef, onSlashThought, onRemoveBlock, onCardReached }: Options) {
  const backend = useBackend()
  const [rev, setRev] = useState<Record<string, number>>({})
  const bumpRev = useCallback((id: string) => setRev((r) => ({ ...r, [id]: (r[id] ?? 0) + 1 })), [])
  const pendingFocus = useRef<PendingFocus | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [focusedId, setFocusedId] = useState<string | null>(null)
  /** The editable the caret was last in (it stays after blur), for inserts and the "+". */
  const [caretId, setCaretId] = useState<string | null>(null)
  const lastEditable = useRef<string | null>(null)
  const [slash, setSlash] = useState<SlashState | null>(null)
  /** What the "/" menu lists: thoughts matching its query (the latest while it's empty); null while loading. */
  const [slashThoughts, setSlashThoughts] = useState<Thought[] | null>(null)

  const focusLater = useCallback((id: string, where: 'start' | 'end' | number = 'start', scene = false) => {
    pendingFocus.current = { id, where, scene }
  }, [])

  useLayoutEffect(() => {
    const p = pendingFocus.current
    if (!p) return
    const el = p.scene ? sceneLineEl(p.id) : blockEl(p.id)
    if (el) {
      pendingFocus.current = null
      if (typeof p.where === 'number') placeCaretAt(el, p.where)
      else placeCaret(el, p.where)
    }
  })

  // Clicking outside any block clears the selection and the "/" menu.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const t = e.target as Element | null
      if (!t?.closest?.('[data-block-id],[data-keep-selection]')) {
        setSelectedId(null)
        setSlash(null)
      }
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [])

  // ── "/" menu: your thoughts ────────────────────────────────────────────
  const slashQuery = slash ? slash.query.trim() : null
  useEffect(() => {
    if (slashQuery === null) {
      setSlashThoughts(null)
      return
    }
    let alive = true
    const t = setTimeout(
      () => {
        backend
          .listThoughts({ search: slashQuery || undefined, limit: SLASH_LIMIT })
          .then((r) => alive && setSlashThoughts(r))
          .catch(() => alive && setSlashThoughts([]))
      },
      slashQuery ? 120 : 0,
    )
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [backend, slashQuery])

  const items = useMemo<Thought[]>(() => slashThoughts ?? [], [slashThoughts])

  /** Close the "/" menu and take its "/query" out of the text. Returns the query it had. */
  const closeSlash = (): string | null => {
    const s = slash
    if (!s) return null
    const el = blockEl(s.blockId)
    if (el) {
      removeSlashToken(el, s.query)
      const html = el.innerHTML
      setBlocks((bs) => bs.map((b) => (b.id === s.blockId ? ({ ...b, html } as Block) : b)))
    }
    setSlash(null)
    return s.query
  }

  const chooseSlash = (thought: Thought) => {
    const s = slash
    if (!s) return
    closeSlash()
    onSlashThought(thought, s.blockId)
  }

  // ── Selecting non-text blocks ──────────────────────────────────────────
  const selectBlock = (id: string, focus = true) => {
    setSelectedId(id)
    setSlash(null)
    if (!focus) return
    const active = document.activeElement as HTMLElement | null
    if (active?.isContentEditable) active.blur()
    selectableEl(id)?.focus({ preventScroll: true })
  }

  const onSelectableKey = (e: KeyboardEvent<HTMLElement>, id: string) => {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Backspace' || e.key === 'Delete') {
      e.preventDefault()
      onRemoveBlock(id)
    } else if (e.key === 'Escape') {
      setSelectedId(null)
      e.currentTarget.blur()
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const bs = docRef.current.blocks
      const next = bs.slice(bs.findIndex((b) => b.id === id) + 1).find(isEditable)
      if (next) {
        setSelectedId(null)
        placeCaret(blockEl(next.id)!, 'start')
      }
    }
  }

  // ── Paragraph editing ──────────────────────────────────────────────────
  /** Turn a paragraph into an empty heading of `level`, caret in it. */
  const toHeading = (id: string, level: 1 | 2 | 3) => {
    setBlocks((list) => list.map((b) => (b.id === id ? { id, type: 'heading', html: '', level } : b)))
    bumpRev(id)
    focusLater(id, 'start')
  }

  const onInput = (block: EditableBlock, el: HTMLElement) => {
    if ((el.textContent ?? '') === '' && el.innerHTML !== '') el.innerHTML = '' // Chrome's leftover <br>
    // Markdown as you type: "# ", "## " or "### " at the start of an empty paragraph makes it a heading.
    const typed = (el.textContent ?? '').replace(/ /g, ' ')
    if (block.type === 'text' && / $/.test(typed)) {
      const level = markdownHeadingLevel(typed)
      if (level) {
        toHeading(block.id, level)
        return
      }
    }
    const html = el.innerHTML
    setBlocks((bs) => bs.map((b) => (b.id === block.id ? ({ ...b, html } as Block) : b)))
    if (block.type === 'text') {
      const q = slashQueryBefore(textBeforeCaret(el))
      setSlash(q !== null ? { blockId: block.id, query: q, index: 0 } : null)
    } else if (slash) {
      setSlash(null)
    }
  }

  /** Put a new paragraph after `id`'s group, so margin notes and unfolds stay with the block they belong to. */
  const insertParagraphs = (list: Block[], id: string, html: string, extra: Block[]) => {
    const out = list.map((b) => (b.id === id ? ({ ...b, html } as Block) : b))
    out.splice(groupEnd(out, out.findIndex((b) => b.id === id)) + 1, 0, ...extra)
    return out
  }

  const onKeyDown = (e: KeyboardEvent<HTMLElement>, block: EditableBlock) => {
    const el = e.currentTarget
    if (slash && slash.blockId === block.id) {
      const n = items.length
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlash(null)
        return
      }
      if (n > 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault()
        const step = e.key === 'ArrowDown' ? 1 : -1
        setSlash({ ...slash, index: (Math.min(slash.index, n - 1) + step + n) % n })
        return
      }
      if (n > 0 && (e.key === 'Enter' || e.key === 'Tab')) {
        e.preventDefault()
        chooseSlash(items[Math.min(slash.index, n - 1)])
        return
      }
    }

    const bs = docRef.current.blocks
    const idx = bs.findIndex((b) => b.id === block.id)

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      const level = block.type === 'text' ? markdownHeadingLevel(el.textContent ?? '') : null
      if (level) {
        toHeading(block.id, level)
        return
      }
      // An empty last line in the last column cell: Enter steps out below the columns.
      const out = (el.textContent ?? '') === '' ? leaveColumns(bs, idx) : null
      if (out) {
        setBlocks(() => out.blocks)
        focusLater(out.focusId, 'start')
        return
      }
      if (isCollapsed() && caretAtStart(el) && (el.textContent ?? '').trim() !== '') {
        // Enter at the very start opens a paragraph above, so this block keeps
        // its text together with its margin notes, unfolds and narration mark.
        const above: TextBlock = { id: newBlockId(), type: 'text', html: '' }
        setBlocks((list) => {
          const out = list.slice()
          out.splice(out.findIndex((b) => b.id === block.id), 0, above)
          return out
        })
        focusLater(block.id, 'start')
        return
      }
      let after = splitAtCaret(el)
      const html = el.innerHTML
      // Splitting inside unfold words leaves the anchor on both halves; it stays with the first.
      for (const anchor of unfoldAnchorsIn(html)) after = unwrapUnfold(after, anchor)
      const nb: TextBlock = { id: newBlockId(), type: 'text', html: after }
      setBlocks((list) => insertParagraphs(list, block.id, html, [nb]))
      focusLater(nb.id, 'start')
      return
    }

    if (e.key === 'Backspace' && isCollapsed() && caretAtStart(el)) {
      if (block.type !== 'text') {
        // A heading or pull quote steps back down to a paragraph first.
        e.preventDefault()
        const html = el.innerHTML
        setBlocks((list) => list.map((b) => (b.id === block.id ? { id: block.id, type: 'text', html } : b)))
        bumpRev(block.id)
        focusLater(block.id, 'start')
        return
      }
      // At a column cell's edge: stay in the cell, step into the columns from below, or take back empty columns.
      const edge = backspaceAt(bs, idx, (el.textContent ?? '') === '')
      if (edge.kind === 'stay') {
        e.preventDefault()
        return
      }
      if (edge.kind === 'removeGroup') {
        e.preventDefault()
        const g = groupOf(bs, edge.startId)
        setBlocks((list) => deleteColumns(list, edge.startId))
        const target = g ? (bs.slice(0, g.start).reverse().find(isEditable) ?? bs.slice(g.end + 1).find(isEditable)) : undefined
        if (target) focusLater(target.id, 'end')
        return
      }
      if (edge.kind === 'removeCell') {
        // An empty column goes; one left over goes back to the page.
        e.preventDefault()
        setBlocks((list) => removeCell(list, edge.markerId))
        if (edge.focus) focusLater(edge.focus.id, edge.focus.at)
        return
      }
      if (edge.kind === 'caret') {
        e.preventDefault()
        if (edge.removeSelf) {
          setBlocks((list) => ensureTrailingText(list.filter((b) => b.id !== block.id)))
          focusLater(edge.id, 'end')
        } else {
          const target = blockEl(edge.id)
          if (target) placeCaret(target, 'end')
        }
        return
      }
      // The column block above (skipping margin notes, pins and unfolds stored after it).
      const a = anchorIndex(bs, idx)
      if (a === null) return
      const prev = bs[a]
      e.preventDefault()
      if (isEditable(prev)) {
        const prevEl = blockEl(prev.id)
        if (!prevEl) return
        mergeInto(prevEl, el)
        const merged = prevEl.innerHTML
        setBlocks((list) => list.filter((b) => b.id !== block.id).map((b) => (b.id === prev.id ? ({ ...b, html: merged } as Block) : b)))
        return
      }
      if ((el.textContent ?? '') === '') setBlocks((list) => ensureTrailingText(list.filter((b) => b.id !== block.id)))
      selectBlock(prev.id)
      onCardReached?.(prev.id)
      return
    }

    // Delete at the very end: the mirror of Backspace at the start. The next
    // paragraph joins this one; a card after it is selected, and the next
    // Delete (or Backspace) removes it.
    if (e.key === 'Delete' && isCollapsed() && caretAtEnd(el)) {
      // Nothing comes across a column cell's edge.
      if (deleteForwardBlocked(bs, idx)) {
        e.preventDefault()
        return
      }
      const next = bs.slice(groupEnd(bs, idx) + 1).find((b) => !isOutOfFlow(b))
      if (!next) return
      e.preventDefault()
      if (isEditable(next)) {
        const nextEl = blockEl(next.id)
        if (!nextEl) return
        mergeInto(el, nextEl)
        const merged = el.innerHTML
        setBlocks((list) => list.filter((b) => b.id !== next.id).map((b) => (b.id === block.id ? ({ ...b, html: merged } as Block) : b)))
        return
      }
      if (block.type === 'text' && (el.textContent ?? '') === '') setBlocks((list) => list.filter((b) => b.id !== block.id))
      selectBlock(next.id)
      onCardReached?.(next.id)
      return
    }

    if (e.key === 'ArrowUp' && caretAtStart(el)) {
      const prev = bs.slice(0, idx).reverse().find(isEditable)
      if (prev) {
        e.preventDefault()
        placeCaret(blockEl(prev.id)!, 'end')
      } else if (subtitleRef.current) {
        e.preventDefault()
        placeCaret(subtitleRef.current, 'end')
      }
      return
    }
    if (e.key === 'ArrowDown' && caretAtEnd(el)) {
      const next = bs.slice(idx + 1).find(isEditable)
      if (next) {
        e.preventDefault()
        placeCaret(blockEl(next.id)!, 'start')
      }
    }
  }

  const onPaste = (e: ClipboardEvent<HTMLElement>, block: EditableBlock) => {
    const text = e.clipboardData.getData('text/plain')
    e.preventDefault()
    const el = e.currentTarget
    // Never through a selection that runs across paragraphs (editing is off
    // then, `data-range-off`): that's useCrossBlockSelection's to replace,
    // and editing this paragraph through it would reach into the rows
    // around it, which React's next render crashes on.
    if (el.getAttribute('contenteditable') !== 'true') return
    let r = currentRange()
    if (!r || !el.contains(r.startContainer) || !el.contains(r.endContainer)) {
      // No caret here (only from code): paste at the end.
      r = endOf(el)
      window.getSelection()?.removeAllRanges()
      window.getSelection()?.addRange(r)
    }
    const parts = pastedParts(e.clipboardData.getData('text/html'), text)
    if (!parts.length) return
    if (block.type !== 'text' || (parts.length === 1 && parts[0].kind === 'text')) {
      insertPlainText(block.type === 'text' ? parts.map((p) => p.text).join(' ') : text.replace(/\s*\n\s*/g, ' '))
      onInput(block, el)
      return
    }
    // Several paragraphs, or headings (lib/paste.ts keeps them): they replace
    // the selection as one undoable operation (blockOps.pasteAcross). A first
    // paragraph joins this one; a heading pasted into an empty line turns the
    // line into it; the rest follow as their own blocks, and whatever was
    // after the selection comes after them. Read off the page, not cut out of
    // it, so ⌘Z puts back what was selected.
    const head = htmlBeside(el, 'before', r.startContainer, r.startOffset)
    const tail = htmlBeside(el, 'after', r.endContainer, r.endOffset)
    const res = pasteAcross(docRef.current.blocks, block.id, block.id, head, tail, parts, newBlockId)
    blockOp(() => res.blocks)
    bumpRev(block.id)
    focusLater(res.focus.id, res.focus.afterHead ? textBeforeCaret(el).length + res.focus.at : res.focus.at)
  }

  const onFocus = (b: EditableBlock) => {
    setFocusedId(b.id)
    setCaretId(b.id)
    lastEditable.current = b.id
    setSelectedId(null)
  }
  const onBlur = (b: EditableBlock) => setFocusedId((f) => (f === b.id ? null : f))

  /** Forget everything tied to the old blocks (after a restore). */
  const reset = useCallback(() => {
    setSelectedId(null)
    setSlash(null)
    setCaretId(null)
    setFocusedId(null)
    lastEditable.current = null
  }, [])

  return {
    rev,
    bumpRev,
    focusLater,
    selectedId,
    setSelectedId,
    selectBlock,
    onSelectableKey,
    focusedId,
    caretId,
    lastEditable,
    slash,
    setSlashIndex: (i: number) => setSlash((s) => (s ? { ...s, index: i } : s)),
    slashThoughts,
    chooseSlash,
    closeSlash,
    editable: { onInput, onKeyDown, onPaste, onFocus, onBlur },
    reset,
  }
}
